import React from 'react';

import { FilterTypes } from '@sharedCR/coreDesignSystem/Table/FilterBaseCDS/FilterBaseCDS.constants';

import { alwaysTrue, getConditionalObject } from 'utils/data';

import BrokerStatusCell from './components/BrokerStatusCell';
import CategoryCell from './components/CategoryCell';
import ConditionCell from './components/ConditionCell';
import DateTimeCell from './components/DateTimeCell';
import ExpandToggleButton from './components/ExpandToggleButton';
import RowActionButton from './components/RowActionButton';
import StatusCell from './components/StatusCell';
import TasksGlanceCell from './components/TasksGlanceCell';
import {
  ConditionStatus,
  TableCellId,
  tableCellMinWidthMap,
  tableCellWidthMap,
} from './Conditions.constants';
import { getRowCellStyles, messageCellStyles } from './Conditions.styles';

const getCellStyles = row => getRowCellStyles({ status: row[TableCellId.STATUS], ConditionStatus });

const getMessageCellStyles = row => ({
  ...messageCellStyles,
  ...getCellStyles(row),
});

export const getConditionsTableConfig = ({
  isBrokerCompany,
  withDeletedDateCell,
  isManageAllowed,
  isEditAllowed,
  isSignOffAllowed,
  isUnsignOffAllowed,
  isRejectAllowed,
  isLoanCanceled,
  loanId,
  expandedRowId,
  categoryOptions,
  statusOptions,
  onToggleExpand,
}) => ({
  [TableCellId.EXPAND]: {
    label: '',
    width: tableCellWidthMap[TableCellId.EXPAND],
    align: 'center',
    customStyles: getCellStyles,
    content: ({ id, _relatedTasks }) => {
      if (!_relatedTasks?.length) {
        return null;
      }

      return (
        <ExpandToggleButton
          isExpanded={expandedRowId === id}
          rowId={id}
          onToggle={onToggleExpand}
        />
      );
    },
  },
  ...getConditionalObject(!isBrokerCompany, {
    [TableCellId.CONDITION]: {
      label: 'Condition',
      width: tableCellWidthMap[TableCellId.CONDITION],
      minWidth: tableCellMinWidthMap[TableCellId.CONDITION],
      sort: true,
      sortBy: TableCellId.CANONICAL_TITLE,
      customStyles: getMessageCellStyles,
      content: ({
        [TableCellId.NUMBER]: number,
        [TableCellId.CANONICAL_TITLE]: title,
        [TableCellId.MESSAGE]: message,
        [TableCellId.ADDED_BY]: addedBy,
        [TableCellId.ADDED_DATE]: addedDate,
      }) => (
        <ConditionCell
          addedBy={addedBy}
          addedDate={addedDate}
          message={message}
          number={number}
          title={title}
        />
      ),
    },
  }),
  ...getConditionalObject(isBrokerCompany, {
    [TableCellId.MESSAGE]: {
      label: 'Message',
      width: tableCellWidthMap[TableCellId.MESSAGE],
      minWidth: tableCellMinWidthMap[TableCellId.SUB_COMPANY_MESSAGE],
      sort: true,
      customStyles: getMessageCellStyles,
      renderContentAsText: alwaysTrue,
      content: ({ [TableCellId.MESSAGE]: cellData }) => cellData,
    },
  }),
  [TableCellId.CATEGORY]: {
    label: 'Category',
    width: tableCellWidthMap[TableCellId.CATEGORY],
    minWidth: tableCellMinWidthMap[TableCellId.CATEGORY],
    sort: true,
    filter: {
      selectOptions: categoryOptions,
      filterType: FilterTypes.MULTI_SELECT,
    },
    customStyles: getCellStyles,
    content: ({ [TableCellId.CATEGORY]: cellData }) => <CategoryCell category={cellData} />,
  },
  ...getConditionalObject(isBrokerCompany, {
    [TableCellId.SUB_COMPANY_MESSAGE]: {
      label: 'Internal Text',
      width: tableCellWidthMap[TableCellId.SUB_COMPANY_MESSAGE],
      minWidth: tableCellMinWidthMap[TableCellId.SUB_COMPANY_MESSAGE],
      customStyles: getMessageCellStyles,
      renderContentAsText: alwaysTrue,
      content: ({ [TableCellId.SUB_COMPANY_MESSAGE]: cellData }) => cellData,
    },
  }),
  [TableCellId.PRIOR_TO]: {
    label: 'Prior To',
    width: tableCellWidthMap[TableCellId.PRIOR_TO],
    minWidth: tableCellMinWidthMap[TableCellId.PRIOR_TO],
    sort: true,
    customStyles: getCellStyles,
    renderContentAsText: alwaysTrue,
    content: ({ [TableCellId.PRIOR_TO]: cellData }) => cellData,
  },
  ...getConditionalObject(isBrokerCompany, {
    [TableCellId.BROKER_STATUS]: {
      label: 'Status',
      width: tableCellWidthMap[TableCellId.BROKER_STATUS],
      minWidth: tableCellMinWidthMap[TableCellId.BROKER_STATUS],
      customStyles: getCellStyles,
      content: ({ [TableCellId.BROKER_STATUS]: cellData }) => (
        <BrokerStatusCell status={cellData} />
      ),
    },
  }),
  ...getConditionalObject(withDeletedDateCell, {
    [TableCellId.DELETED_DATE]: {
      label: 'Deleted Date',
      width: tableCellWidthMap[TableCellId.DELETED_DATE],
      minWidth: tableCellMinWidthMap[TableCellId.DELETED_DATE],
      sort: true,
      customStyles: getCellStyles,
      content: ({ [TableCellId.DELETED_DATE]: cellData }) => <DateTimeCell value={cellData} />,
    },
  }),
  ...getConditionalObject(!isBrokerCompany, {
    [TableCellId.STATUS]: {
      label: 'Status',
      width: tableCellWidthMap[TableCellId.STATUS],
      minWidth: tableCellMinWidthMap[TableCellId.STATUS],
      filter: {
        selectOptions: statusOptions,
        filterType: FilterTypes.MULTI_SELECT,
      },
      customStyles: getCellStyles,
      content: ({
        [TableCellId.STATUS]: status,
        [TableCellId.SIGNED_OFF_BY]: signedOffBy,
        [TableCellId.SIGNED_OFF_DATE]: signedOffDate,
      }) => <StatusCell signedOffBy={signedOffBy} signedOffDate={signedOffDate} status={status} />,
    },
  }),
  [TableCellId.TASKS_COUNT]: {
    label: 'Tasks',
    width: tableCellWidthMap[TableCellId.TASKS_COUNT],
    minWidth: tableCellMinWidthMap[TableCellId.TASKS_COUNT],
    align: 'left',
    customStyles: getCellStyles,
    content: ({ [TableCellId.TASKS_COUNT]: cellData }) => <TasksGlanceCell {...cellData} />,
  },
  [TableCellId.SLA]: {
    label: 'SLA',
    width: tableCellWidthMap[TableCellId.SLA],
    minWidth: tableCellMinWidthMap[TableCellId.SLA],
    customStyles: getCellStyles,
    renderContentAsText: alwaysTrue,
    content: ({ [TableCellId.SLA]: cellData }) => cellData,
  },
  [TableCellId.ACTIONS]: {
    label: 'Actions',
    width: tableCellWidthMap[TableCellId.ACTIONS],
    align: 'center',
    customStyles: getCellStyles,
    content: ({ _condition }) => (
      <RowActionButton
        condition={_condition}
        isBrokerCompany={isBrokerCompany}
        isEditAllowed={isEditAllowed}
        isLoanCanceled={isLoanCanceled}
        isManageAllowed={isManageAllowed}
        isRejectAllowed={isRejectAllowed}
        isSignOffAllowed={isSignOffAllowed}
        isUnsignOffAllowed={isUnsignOffAllowed}
        loanId={loanId}
      />
    ),
  },
});
